// src/store/slices/preferencesSlice.ts
/**
 * User preferences with localStorage persistence
 * Implements Section 5.9 of the specification
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PreferencesState, LocaleCode, ThemeMode, SubdivisionMode } from '../../types/redux';

const PREFERENCES_STORAGE_KEY = 'nl:preferences';

// Default preferences
const defaultPreferences: PreferencesState = {
  theme: 'light',
  locale: 'en-GB',
  exportBackground: 'transparent',
  autoSave: true,
  keyboardShortcuts: true,
  accessibilityMode: false,
  constantsPrecision: 3,
  defaultScaleFactor: { n: 2, d: 1 }, // 2x by default
  preferredOrientation: 'horizontal',
  showInstallPrompt: true, // Show install prompt by default
  defaultJumpSize: 10, // Default jump size of 10 units
  wheelReverseScroll: false,
  wheelScrollSensitivity: 1,
  sidebarDefaultOpen: true, // Sidebar starts open on desktop by default
  textScale: 1,
  subdivisionMode: 'smart', // Smart/flexible subdivision as default
};

/**
 * Load preferences from localStorage
 */
function loadPreferencesFromStorage(): PreferencesState {
  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new preference additions
      const preferences = { ...defaultPreferences, ...parsed };
      let updated = false;

      // Migrate legacy defaults to new defaults while respecting user changes
      if (preferences.defaultJumpSize === 1) {
        preferences.defaultJumpSize = 10;
        updated = true;
      }
      if (
        preferences.defaultScaleFactor?.n === 1 &&
        preferences.defaultScaleFactor?.d === 2
      ) {
        preferences.defaultScaleFactor = { n: 2, d: 1 };
        updated = true;
      }
      if (preferences.constantsPrecision === 4) {
        preferences.constantsPrecision = 3;
        updated = true;
      }

      if (updated) {
        savePreferencesToStorage(preferences);
      }
      console.log('📋 Loaded preferences:', preferences);
      return preferences;
    }
  } catch (error) {
    console.warn('Failed to load preferences from storage:', error);
  }
  console.log('📋 Using default preferences:', defaultPreferences);
  return defaultPreferences;
}

/**
 * Save preferences to localStorage
 */
function savePreferencesToStorage(preferences: PreferencesState): void {
  try {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save preferences to storage:', error);
  }
}

const initialState: PreferencesState = loadPreferencesFromStorage();

export const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    /**
     * Set theme mode
     */
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
      savePreferencesToStorage(state);
    },

    /**
     * Toggle theme between light and dark
     */
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
      savePreferencesToStorage(state);
    },

    /**
     * Set locale for number formatting
     */
    setLocale: (state, action: PayloadAction<LocaleCode>) => {
      state.locale = action.payload;
      savePreferencesToStorage(state);
    },

    /**
     * Set export background preference
     */
    setExportBackground: (state, action: PayloadAction<'transparent' | 'white' | 'black'>) => {
      state.exportBackground = action.payload;
      savePreferencesToStorage(state);
    },

    /**
     * Toggle auto-save functionality
     */
    toggleAutoSave: (state) => {
      state.autoSave = !state.autoSave;
      savePreferencesToStorage(state);
    },

    /**
     * Toggle keyboard shortcuts
     */
    toggleKeyboardShortcuts: (state) => {
      state.keyboardShortcuts = !state.keyboardShortcuts;
      savePreferencesToStorage(state);
    },

    /**
     * Toggle accessibility mode
     */
    toggleAccessibilityMode: (state) => {
      state.accessibilityMode = !state.accessibilityMode;
      savePreferencesToStorage(state);
    },

    /**
     * Set constants precision (2-6 decimal places)
     */
    setConstantsPrecision: (state, action: PayloadAction<number>) => {
      state.constantsPrecision = Math.max(2, Math.min(6, action.payload));
      savePreferencesToStorage(state);
    },

    /**
     * Set default scale factor for double number lines
     */
    setDefaultScaleFactor: (state, action: PayloadAction<{ n: number; d: number }>) => {
      state.defaultScaleFactor = action.payload;
      savePreferencesToStorage(state);
    },

    /**
     * Set preferred orientation
     */
    setPreferredOrientation: (state, action: PayloadAction<'horizontal' | 'vertical'>) => {
      state.preferredOrientation = action.payload;
      savePreferencesToStorage(state);
    },

    /**
     * Set default jump size for pan toolbar
     */
    setDefaultJumpSize: (state, action: PayloadAction<number>) => {
      state.defaultJumpSize = Math.max(0.1, Math.min(100, action.payload)); // Clamp between 0.1 and 100
      savePreferencesToStorage(state);
    },

    /** Toggle wheel reverse scroll for panning */
    toggleWheelReverseScroll: (state) => {
      state.wheelReverseScroll = !state.wheelReverseScroll;
      savePreferencesToStorage(state);
    },

    /** Set wheel scroll sensitivity multiplier */
    setWheelScrollSensitivity: (state, action: PayloadAction<number>) => {
      const value = action.payload;
      state.wheelScrollSensitivity = Math.max(0.1, Math.min(4, Number.isFinite(value) ? value : 1));
      savePreferencesToStorage(state);
    },

    /**
     * Toggle install prompt visibility
     */
    toggleInstallPrompt: (state) => {
      state.showInstallPrompt = !state.showInstallPrompt;
      savePreferencesToStorage(state);
    },

    /**
     * Set whether the sidebar (left toolbar) starts open on desktop
     */
    setSidebarDefaultOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarDefaultOpen = action.payload;
      savePreferencesToStorage(state);
    },

    /**
     * Set global text scale (0.5–2.0)
     */
    setTextScale: (state, action: PayloadAction<number>) => {
      const value = action.payload;
      const clamped = Math.max(0.5, Math.min(2, Number.isFinite(value) ? value : 1));
      state.textScale = clamped;
      savePreferencesToStorage(state);
    },

    /**
     * Set subdivision mode for hierarchical ticks
     * Controls how tick marks are subdivided when zooming in
     */
    setSubdivisionMode: (state, action: PayloadAction<SubdivisionMode>) => {
      state.subdivisionMode = action.payload;
      savePreferencesToStorage(state);
    },

    /**
     * Reset all preferences to defaults
     */
    resetPreferences: (state) => {
      Object.assign(state, defaultPreferences);
      savePreferencesToStorage(state);
    },

    /**
     * Bulk update preferences (for loading from share)
     */
    updatePreferences: (state, action: PayloadAction<Partial<PreferencesState>>) => {
      Object.assign(state, action.payload);
      savePreferencesToStorage(state);
    },
  },
});

export const {
  setTheme,
  toggleTheme,
  setLocale,
  setExportBackground,
  toggleAutoSave,
  toggleKeyboardShortcuts,
  toggleAccessibilityMode,
  setConstantsPrecision,
  setDefaultScaleFactor,
  setPreferredOrientation,
  setDefaultJumpSize,
  toggleWheelReverseScroll,
  setWheelScrollSensitivity,
  toggleInstallPrompt,
  setSidebarDefaultOpen,
  setTextScale,
  setSubdivisionMode,
  resetPreferences,
  updatePreferences,
} = preferencesSlice.actions;

export default preferencesSlice.reducer;

// Selectors
export const selectTheme = (state: { preferences: PreferencesState }) => state.preferences.theme;
export const selectLocale = (state: { preferences: PreferencesState }) => state.preferences.locale;
export const selectExportBackground = (state: { preferences: PreferencesState }) => 
  state.preferences.exportBackground;
export const selectConstantsPrecision = (state: { preferences: PreferencesState }) => 
  state.preferences.constantsPrecision;
export const selectSubdivisionMode = (state: { preferences: PreferencesState }) => 
  state.preferences.subdivisionMode ?? 'smart';
export const selectTextScale = (state: { preferences: PreferencesState }) => 
  state.preferences.textScale ?? 1;